/**
 * Hook for tracking time spent on pages
 */

import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "@/analytics/AnalyticsContext";
import { VISITOR_EVENTS } from "@/analytics/events";

export function usePageTimer(
  userId?: string,
  isAuthenticated: boolean = false
) {
  const { trackEvent } = useAnalytics();
  const location = useLocation();
  const pageStartTime = useRef<Date | null>(null);
  const currentPath = useRef<string | null>(null);

  const trackPageTime = useCallback(
    (pagePath: string) => {
      if (pageStartTime.current) {
        const now = new Date();
        const timeSpentMs = now.getTime() - pageStartTime.current.getTime();
        const timeSpentSeconds = Math.round(timeSpentMs / 1000);

        if (timeSpentSeconds > 1) {
          trackEvent(VISITOR_EVENTS.PAGE_TIME_SPENT, {
            user_id: userId,
            page_path: pagePath,
            page_title: document.title,
            time_spent_seconds: timeSpentSeconds,
            is_authenticated: isAuthenticated,
          });
        }
      }
    },
    [userId, isAuthenticated, trackEvent]
  );

  useEffect(() => {
    const newPath = location.pathname;

    if (currentPath.current && currentPath.current !== newPath) {
      trackPageTime(currentPath.current);
    }

    pageStartTime.current = new Date();
    currentPath.current = newPath;
  }, [location.pathname, trackPageTime]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentPath.current) {
        trackPageTime(currentPath.current);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && currentPath.current) {
        trackPageTime(currentPath.current);
        pageStartTime.current = new Date();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [trackPageTime]);

  return {
    trackCurrentPageTime: () => {
      if (currentPath.current) {
        trackPageTime(currentPath.current);
      }
    },
  };
}
